// Stock Reservation — lock available Passed lots against a Sales Order /
// Work Order / Department. FIFO allocation on the backend prevents any two
// reservations from double-booking the same stock.
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import RequiredVsActual from "@/components/RequiredVsActual";
import { RowActions } from "@/components/RowActions";

const TYPES = ["Sales Order", "Work Order", "Department", "Other"];

export default function StockReservation() {
  const [rms, setRms] = useState([]);
  const [rows, setRows] = useState([]);
  const [lotMap, setLotMap] = useState({});   // lot_id → { lot_no, batch_no }
  const [rmId, setRmId] = useState("");
  const [qty, setQty] = useState(0);
  const [rtype, setRtype] = useState("Sales Order");
  const [orderId, setOrderId] = useState("");
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [{ data: r }, { data: res }, { data: lots }] = await Promise.all([
      api.get("/raw-materials/"),
      api.get("/stock/reservations?status=Active"),
      api.get("/inventory-lots/").catch(() => ({ data: [] })),
    ]);
    // Mar 2026 · Universal Deactivation Filter — hide inactive RMs.
    setRms((r || []).filter(x => x.is_active !== false));
    setRows(res || []);
    // Feb 2026 · Build a lot_id → { lot_no, batch_no } map so the Lots column
    // can hydrate batch numbers for legacy reservation rows that only stored
    // lot_id (or lot_id + lot_no) without batch metadata.
    const m = {};
    (lots || []).forEach(l => { m[l.id] = { lot_no: l.lot_no || "", batch_no: l.batch_no || "" }; });
    setLotMap(m);
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!rmId || qty <= 0) { toast.error("Pick a RM + qty > 0"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/stock/reservations", {
        rm_id: rmId, quantity: qty, reservation_type: rtype, order_id: orderId, remarks,
      });
      toast.success(`Reserved · ${data.allocations.length} lot(s) locked`);
      setQty(0); setOrderId(""); setRemarks(""); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reservation failed"); }
    setBusy(false);
  };

  const release = async (id) => {
    if (!window.confirm("Unreserve this lock? Lots will return to the free-stock pool and become available for other orders.")) return;
    try { await api.delete(`/stock/reservations/${id}`); toast.success("Unreserved · stock returned to free pool"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Unreserve failed"); }
  };

  const rmName = (id) => rms.find(r => r.id === id)?.code || (id ? String(id).slice(0, 8) : "—");
  const inp = "w-full h-10 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)]";

  // Feb 2026 · UUIDs used to leak into the Order column for GRN-QC-Pass
  // rows (legacy `order_id` = raw sales_order_id). Prefer the explicit
  // `sales_order_no` / `work_order_no` fields; when absent fall back to
  // `reference` (which pre-2026 auto-reservations already stamped) and
  // only ever show `order_id` when it clearly isn't a UUID.
  const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const displayOrderNo = (r) => {
    const explicit = r.sales_order_no || r.work_order_no;
    if (explicit) return explicit;
    const oid = r.order_id || "";
    if (oid && !UUID_RE.test(oid)) return oid;
    if (r.reference && !UUID_RE.test(r.reference) && r.reference !== r.mrp_no) return r.reference;
    return "—";
  };

  // Feb 2026 · Unified Reservation Ledger — colour-code the source chip so
  // planners instantly see which lock came from Intelligent Auto-Allocation
  // (PO or Opening Stock) versus a manual MRP tick versus a legacy manual
  // reservation. Falls back to grey when the ledger row pre-dates tagging.
  const SOURCE_STYLES = {
    "Auto-PO":        "bg-cyan-500/15 text-cyan-300 border-cyan-500/40",
    "Auto-Opening":   "bg-violet-500/15 text-violet-300 border-violet-500/40",
    "Auto-MRP":       "bg-orange-500/15 text-orange-300 border-orange-500/40",
    "Manual-MRP":     "bg-amber-500/15 text-amber-300 border-amber-500/40",
    "GRN QC-Pass":    "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  };
  const sourceLabel = (r) => {
    if (r.source) return r.source;
    if (r.auto_source === "GRN_QC_PASS") return "GRN QC-Pass";
    return "Manual";
  };
  return (
    <div className="space-y-4" data-testid="reservation-page">
      <div className="overline text-[10px] text-[var(--accent)]">INVENTORY</div>
      <h1 className="text-2xl font-semibold">Stock Reservation Ledger</h1>
      <p className="text-sm text-zinc-400">Unified locked-stock ledger · every MRP allocation (auto or manual) lands here. Release lots back to free stock any time a plan changes.</p>
      <div className="tactical-card p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <label className="text-xs uppercase tracking-widest text-zinc-500 space-y-1">
            <div>Raw Material *</div>
            <select data-testid="res-rm" value={rmId} onChange={e => setRmId(e.target.value)} className={inp}>
              <option value="">— Select —</option>
              {rms.map(r => <option key={r.id} value={r.id}>{r.code} · {r.name}</option>)}
            </select>
          </label>
          <label className="text-xs uppercase tracking-widest text-zinc-500 space-y-1">
            <div>Quantity *</div>
            <input data-testid="res-qty" type="number" step="0.001" min="0" value={qty} onChange={e => setQty(parseFloat(e.target.value)||0)} className={inp} />
          </label>
          <label className="text-xs uppercase tracking-widest text-zinc-500 space-y-1">
            <div>Reservation Type</div>
            <select data-testid="res-type" value={rtype} onChange={e => setRtype(e.target.value)} className={inp}>
              {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label className="text-xs uppercase tracking-widest text-zinc-500 space-y-1">
            <div>Order / Ref ID</div>
            <input data-testid="res-order" value={orderId} onChange={e => setOrderId(e.target.value)} className={inp} placeholder="e.g. SO/25-26/00123" />
          </label>
          <label className="text-xs uppercase tracking-widest text-zinc-500 space-y-1 md:col-span-2">
            <div>Remarks</div>
            <input data-testid="res-remarks" value={remarks} onChange={e => setRemarks(e.target.value)} className={inp} />
          </label>
        </div>
        <div className="flex justify-end">
          <button data-testid="res-submit" onClick={submit} disabled={busy}
            className="h-10 px-6 bg-[var(--accent)] hover:bg-blue-600 text-white text-sm font-semibold rounded-sm disabled:opacity-50">
            {busy ? "Reserving…" : "Reserve Stock"}
          </button>
        </div>
      </div>
      <div className="tactical-card overflow-x-auto">
        <div className="p-4 border-b border-[var(--border)] text-sm font-semibold flex items-center justify-between">
          <span>Active Reservations ({rows.length})</span>
          <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Source chips · Auto-PO · Auto-Opening · Manual-MRP · GRN QC-Pass</span>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]">
              <th className="text-left px-5 py-3">Source</th>
              <th className="text-left px-5 py-3">Required → Actual · Allocation</th>
              <th className="text-left px-5 py-3">WO / MRP</th>
              <th className="text-left px-5 py-3">Qty</th>
              <th className="text-left px-5 py-3">Type</th>
              <th className="text-left px-5 py-3">Order / MRP</th>
              <th className="text-left px-5 py-3">Lots</th>
              <th className="text-left px-5 py-3">Created</th>
              <th className="text-right px-5 py-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan="9" className="text-center px-5 py-6 text-zinc-500">No active reservations</td></tr>}
            {rows.map(r => {
              const src = sourceLabel(r);
              return (
              <tr key={r.id} className="border-b border-[var(--border)] last:border-b-0" data-testid={`res-row-${(r.id || "").slice(0,8)}`}>
                <td className="px-5 py-3">
                  <span data-testid={`res-source-${(r.id || "").slice(0,8)}`}
                        className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm border ${SOURCE_STYLES[src] || "bg-zinc-500/10 text-zinc-300 border-zinc-500/30"}`}>
                    {src}
                  </span>
                </td>
                <td className="px-5 py-3 font-mono text-[11px]">
                  <RequiredVsActual
                    requiredCode={r.required_rm_code || r.rm_code || rmName(r.raw_material_id || r.rm_id)}
                    actualCode={r.rm_code || rmName(r.raw_material_id || r.rm_id)}
                    allocationType={r.allocation_type || "EXACT"}
                    alternateApprovalNo={r.alternate_approval_no}
                    alternatePriority={r.alternate_priority}
                  />
                </td>
                <td className="px-5 py-3 font-mono text-[11px]">
                  <div className="text-zinc-200">{r.work_order_no || "—"}</div>
                  {r.mrp_no && <div className="text-[9px] text-zinc-500 mt-0.5">{r.mrp_no}</div>}
                </td>
                <td className="px-5 py-3 font-bold tabular-nums">{Number(r.quantity ?? r.qty ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</td>
                <td className="px-5 py-3">{r.reservation_type || "—"}</td>
                <td className="px-5 py-3 font-mono text-[11px]">
                  <div data-testid={`res-order-no-${(r.id || "").slice(0,8)}`} className="text-zinc-200">{displayOrderNo(r)}</div>
                  {r.mrp_no && (
                    <div className="text-[9px] text-zinc-500 mt-0.5">{r.mrp_no}</div>
                  )}
                </td>
                <td className="px-5 py-3 text-[11px] max-w-[280px]">
                  <div className="flex flex-wrap gap-1">
                    {(r.allocations || r.selected_lots || []).length === 0 && <span className="text-zinc-500">—</span>}
                    {(r.allocations || r.selected_lots || []).map((a, i) => {
                      const meta = lotMap[a.lot_id] || {};
                      const lotNo = a.lot_no || meta.lot_no || (a.lot_id || "").slice(0, 6);
                      const batchNo = a.batch_no || meta.batch_no || "";
                      return (
                        <span key={i}
                              className="inline-flex flex-col items-start px-1.5 py-0.5 rounded-sm border border-purple-500/30 bg-purple-500/5"
                              title={`${lotNo} · Batch ${batchNo || "—"} · ${a.qty ?? ""}`}>
                          <span className="font-mono text-[10px] text-emerald-300">{lotNo}</span>
                          {batchNo && <span className="font-mono text-[9px] text-purple-300">Batch: {batchNo}</span>}
                        </span>
                      );
                    })}
                  </div>
                  {/* Phase H1-δ · Store Override lineage crumb + MI back-ref
                      appear when a reservation was created via R1 replacement
                      or has been consumed by an MI. Read-only display. */}
                  {r.reallocation_source_id && (
                    <div className="mt-1 text-[9px] text-cyan-300 font-mono truncate"
                          title={`Reallocated from ${r.reallocation_source_id}${r.reallocation_reason ? ` · ${r.reallocation_reason}` : ""}`}
                          data-testid={`res-override-${(r.id || "").slice(0,8)}`}>
                      {r.source === "MI-Reversal-Restore"
                        ? `↩ Restored from ${(r.reallocation_reason || "").replace(/^MI reversal · /, "") || "MI"}`
                        : `↳ Override from ${String(r.reallocation_source_id).slice(0, 8)}`}
                    </div>
                  )}
                  {r.mi_no && (
                    <div className="mt-1 text-[9px] text-amber-300 font-mono"
                          data-testid={`res-mi-${(r.id || "").slice(0,8)}`}>
                      Consumed by {r.mi_no}
                    </div>
                  )}
                </td>
                <td className="px-5 py-3 text-[11px] text-zinc-400">{(r.created_at || "").slice(0, 10)}</td>
                <td className="px-5 py-3 text-right">
                  <RowActions
                    testid={`res-actions-${(r.id || "").slice(0,8)}`}
                    items={[
                      { label: "Unreserve", tone: "danger", testid: `res-release-${(r.id || "").slice(0,8)}`, onClick: () => release(r.id) },
                    ]}
                  />
                </td>
              </tr>
            );})}
          </tbody>
        </table>
      </div>
    </div>
  );
}
